import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { api } from '@/api/client';
import type { FoodSuggestionDto, SavedMealDto, CycleSnapshotDto, WorkoutEntryDto } from '@/api/types';

type Category = 'nav' | 'food' | 'compound' | 'exercise';

interface CommandItem {
  id: string;
  label: string;
  sublabel?: string;
  category: Category;
  action: () => void;
}

interface CommandPaletteContextValue {
  open: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

function fuzzyMatch(needle: string, haystack: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  let ni = 0;
  for (let hi = 0; hi < h.length && ni < n.length; hi++) {
    if (n[ni] === h[hi]) ni++;
  }
  return ni === n.length;
}

const NAV_COMMANDS = [
  { labelKey: 'nav.today', path: '/', sublabelKey: 'components.cmdNavTodaySub' },
  { labelKey: 'nav.food', path: '/food', sublabelKey: 'components.cmdNavFoodSub' },
  { labelKey: 'nav.body', path: '/body', sublabelKey: 'components.cmdNavBodySub' },
  { labelKey: 'nav.train', path: '/train', sublabelKey: 'components.cmdNavTrainSub' },
  { labelKey: 'nav.meds', path: '/meds', sublabelKey: 'components.cmdNavMedsSub' },
  { labelKey: 'nav.plan', path: '/plan', sublabelKey: 'components.cmdNavPlanSub' },
  { labelKey: 'nav.settings', path: '/settings', sublabelKey: 'components.cmdNavSettingsSub' },
];

const CATEGORY_LABEL_KEY: Record<Category, string> = {
  nav: 'components.cmdCategoryPage',
  food: 'components.cmdCategoryFood',
  compound: 'components.cmdCategoryCompound',
  exercise: 'components.cmdCategoryExercise',
};

const IS_MAC = /Mac/.test(navigator.userAgent);

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const { data: savedFoods } = useQuery({
    queryKey: ['food', 'saved'],
    queryFn: ({ signal }) => api.get<FoodSuggestionDto[]>('/api/food/saved', { signal }),
    enabled: isAuthenticated,
  });

  const { data: savedMeals } = useQuery({
    queryKey: ['food', 'savedMeals'],
    queryFn: ({ signal }) => api.get<SavedMealDto[]>('/api/food/saved/meals', { signal }),
    enabled: isAuthenticated,
  });

  const { data: activeCycle } = useQuery({
    queryKey: ['plan', 'active'],
    queryFn: async ({ signal }) => {
      const resp = await fetch('/api/cycle/active', { credentials: 'include', signal });
      if (resp.status === 404) return null;
      if (!resp.ok) throw new Error(`/api/cycle/active failed: ${resp.status}`);
      return (await resp.json()) as CycleSnapshotDto;
    },
    enabled: isAuthenticated,
    retry: false,
  });

  const { data: recentWorkouts } = useQuery({
    queryKey: ['workout', 'recent', 90],
    queryFn: ({ signal }) => api.get<WorkoutEntryDto[]>('/api/workout/recent?days=90', { signal }),
    enabled: isAuthenticated,
  });

  const openPalette = useCallback(() => {
    setIsOpen(true);
    setQuery('');
    setActiveIndex(0);
  }, []);

  const closePalette = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    function handleGlobalKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) closePalette();
        else openPalette();
      }
    }
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [isOpen, openPalette, closePalette]);

  useEffect(() => {
    if (isOpen) requestAnimationFrame(() => inputRef.current?.focus());
  }, [isOpen]);

  const navItems: CommandItem[] = NAV_COMMANDS.map((n) => ({
    id: `nav:${n.path}`,
    label: t(n.labelKey),
    sublabel: t(n.sublabelKey),
    category: 'nav',
    action: () => { navigate(n.path); closePalette(); },
  }));

  const foodItems: CommandItem[] = (savedFoods ?? []).map((f) => ({
    id: `food:${f.name}`,
    label: f.name,
    sublabel: `${f.kcal} kcal · ${f.servingDescription}`,
    category: 'food',
    action: () => { navigate('/food'); closePalette(); },
  }));

  const mealItems: CommandItem[] = (savedMeals ?? []).map((m) => ({
    id: `meal:${m.name}`,
    label: m.name,
    sublabel: t('components.cmdMealSub', { kcal: m.totalKcal }),
    category: 'food',
    action: () => { navigate('/food'); closePalette(); },
  }));

  const compoundNames = new Set<string>();
  const compoundItems: CommandItem[] = [];
  for (const c of activeCycle?.cycle.compoundTracks ?? []) {
    if (!compoundNames.has(c.compoundName)) {
      compoundNames.add(c.compoundName);
      compoundItems.push({
        id: `compound:${c.compoundName}`,
        label: c.compoundName,
        sublabel: `${c.doseDisplay} · ${c.frequency}`,
        category: 'compound',
        action: () => { navigate('/meds'); closePalette(); },
      });
    }
  }

  const exerciseNames = new Set<string>();
  const exerciseItems: CommandItem[] = [];
  for (const w of recentWorkouts ?? []) {
    const name = w.cardioActivity ?? w.kind;
    if (!exerciseNames.has(name)) {
      exerciseNames.add(name);
      exerciseItems.push({
        id: `exercise:${name}`,
        label: name,
        sublabel: w.cardioActivity ? t('components.cmdCardio') : t('components.cmdStrength'),
        category: 'exercise',
        action: () => { navigate('/train'); closePalette(); },
      });
    }
  }

  const allItems: CommandItem[] = [
    ...navItems,
    ...foodItems,
    ...mealItems,
    ...compoundItems,
    ...exerciseItems,
  ];

  const filtered = query
    ? allItems.filter(
        (item) =>
          fuzzyMatch(query, item.label) ||
          (item.sublabel ? fuzzyMatch(query, item.sublabel) : false),
      )
    : navItems;

  const clampedActive = Math.min(activeIndex, Math.max(0, filtered.length - 1));

  useEffect(() => {
    const el = resultsRef.current?.querySelector('[aria-selected="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [clampedActive]);

  function handlePaletteKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case 'Escape':
        closePalette();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        filtered[clampedActive]?.action();
        break;
    }
  }

  return (
    <CommandPaletteContext.Provider value={{ open: openPalette }}>
      {children}
      {isOpen && isAuthenticated && (
        <>
          <div className="cmd-backdrop" onClick={closePalette} />
          <div
            className="cmd-palette"
            role="dialog"
            aria-modal="true"
            aria-label={t('components.cmdPaletteLabel')}
          >
            <div className="cmd-search-row">
              <svg
                className="cmd-search-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                ref={inputRef}
                className="cmd-input"
                type="text"
                placeholder={t('components.cmdSearchPlaceholder')}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={handlePaletteKeyDown}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              {query && (
                <button
                  className="cmd-clear"
                  onClick={() => {
                    setQuery('');
                    inputRef.current?.focus();
                  }}
                  aria-label={t('components.cmdClearSearch')}
                >
                  ×
                </button>
              )}
            </div>
            <div className="cmd-results" role="listbox" ref={resultsRef}>
              {filtered.length === 0 ? (
                <div className="cmd-empty">{t('components.cmdNoResults', { query })}</div>
              ) : (
                filtered.map((item, idx) => (
                  <button
                    key={item.id}
                    className={`cmd-item${idx === clampedActive ? ' cmd-item--active' : ''}`}
                    role="option"
                    aria-selected={idx === clampedActive}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={item.action}
                  >
                    <span className={`cmd-item__badge cmd-item__badge--${item.category}`}>
                      {t(CATEGORY_LABEL_KEY[item.category])}
                    </span>
                    <span className="cmd-item__label">{item.label}</span>
                    {item.sublabel && (
                      <span className="cmd-item__sub">{item.sublabel}</span>
                    )}
                  </button>
                ))
              )}
            </div>
            <div className="cmd-footer">
              <span className="cmd-hint"><kbd>↑↓</kbd> {t('components.cmdNavigate')}</span>
              <span className="cmd-hint"><kbd>↵</kbd> {t('components.cmdSelect')}</span>
              <span className="cmd-hint"><kbd>Esc</kbd> {t('components.cmdCloseHint')}</span>
              <span className="cmd-hint cmd-hint--right">{t('components.cmdToggleHint', { key: IS_MAC ? '⌘K' : 'Ctrl+K' })}</span>
            </div>
          </div>
        </>
      )}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette() {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used inside CommandPaletteProvider');
  return ctx;
}
